import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DEFAULT_MOOD_TARGETS, useAppStore } from '~/hooks/use-app-store'
import { useAudioElement } from '~/hooks/useAudioElement'
import { useCrossfade } from '~/hooks/useCrossfade'
import { useDesktopNotifications } from '~/hooks/useDesktopNotifications'
import { useInterstitialMode } from '~/hooks/useInterstitialMode'
import { usePlaybackControls } from '~/hooks/usePlaybackControls'
import { useQueueManagement } from '~/hooks/useQueueManagement'
import { useQueuePersistence } from '~/hooks/useQueuePersistence'
import { useRadioMode } from '~/hooks/useRadioMode'
import { streamUrl } from '~/lib/cover-art'

import { getNextIndex, type NextIndexInput } from './next-index'
import { PlayerContext, type PlayerContextValue } from './PlayerContext'
import type { PlayerState, RepeatMode } from './types'
import { BASE_PLAYER_STATE } from './types'

const MAX_CROSSFADE = 12

function clampCrossfade(seconds: number): number {
  return Math.max(0, Math.min(MAX_CROSSFADE, seconds))
}

type TimeSubscriber = () => void

export const PlayerProvider = ({ children }: { children: React.ReactNode }) => {
  // Settings are persisted in Zustand — subscribe to keep in sync
  const playerSettings = useAppStore((s) => s.playerSettings)
  const [state, setState] = useState<PlayerState>(() => ({
    ...BASE_PLAYER_STATE,
    ...useAppStore.getState().playerSettings,
  }))
  const stateRef = useRef(state)
  stateRef.current = state

  // Sync Zustand settings into component state (Zustand is the source of truth)
  useEffect(() => {
    setState((s) => ({ ...s, ...playerSettings }))
  }, [playerSettings])

  const scrobbledRef = useRef(false)
  const restoredRef = useRef(false)
  const skipNextPlayRef = useRef(false)
  const pendingSeekRef = useRef<number | null>(null)
  const pendingRadioAdvanceRef = useRef(false)
  const intentionalPauseRef = useRef(false)
  const currentTrackIdRef = useRef<number | null>(null)
  const currentTimeRef = useRef(0)
  const timeSubscribersRef = useRef(new Set<TimeSubscriber>())

  const getCurrentTime = useCallback(() => currentTimeRef.current, [])

  const subscribeCurrentTime = useCallback((cb: TimeSubscriber) => {
    timeSubscribersRef.current.add(cb)
    return () => {
      timeSubscribersRef.current.delete(cb)
    }
  }, [])

  const notifyTimeSubscribers = useCallback(() => {
    for (const sub of timeSubscribersRef.current) sub()
  }, [])

  const {
    channelARef,
    channelBRef,
    activeChannelRef,
    crossfadeActiveRef,
    crossfadeTimerRef,
    ensureAudioContext,
    getActiveChannel,
    getAnalyserNode,
    applyReplayGain,
    cancelCrossfade,
  } = useAudioElement({
    stateRef,
    currentTrackIdRef,
    scrobbledRef,
    currentTimeRef,
    pendingRadioAdvanceRef,
    intentionalPauseRef,
    notifyTimeSubscribers,
    setState,
    volume: state.volume,
    muted: state.muted,
    replayGainEnabled: state.replayGainEnabled,
    replayGainPreampDb: state.replayGainPreampDb,
  })

  useCrossfade({
    channelARef,
    channelBRef,
    activeChannelRef,
    crossfadeActiveRef,
    crossfadeTimerRef,
    stateRef,
    currentTrackIdRef,
    scrobbledRef,
    ensureAudioContext,
    setState,
  })

  useQueuePersistence({
    queue: state.queue,
    currentIndex: state.currentIndex,
    isPlaying: state.isPlaying,
    stateRef,
    restoredRef,
    skipNextPlayRef,
    pendingSeekRef,
    currentTimeRef,
    setState,
  })

  const { play, pause, resume, togglePlay, next, prev, seek, advanceOne } = usePlaybackControls({
    setState,
    getActiveChannel,
    ensureAudioContext,
    cancelCrossfade,
    applyReplayGain,
    notifyTimeSubscribers,
    currentTrackIdRef,
    scrobbledRef,
    skipNextPlayRef,
    pendingSeekRef,
    pendingRadioAdvanceRef,
    intentionalPauseRef,
    currentTimeRef,
    queue: state.queue,
    currentIndex: state.currentIndex,
    radioMode: state.radioMode,
    shuffle: state.shuffle,
    repeat: state.repeat,
  })

  const { enqueue, addToQueue, insertAt, removeFromQueue, moveInQueue, clearQueue, playTrackAt } =
    useQueueManagement({
      setState,
      cancelCrossfade,
      channelARef,
      channelBRef,
      currentTrackIdRef,
    })

  const setVolume = useCallback((volume: number) => {
    setState((s) => ({ ...s, volume, muted: false }))
  }, [])

  const toggleMute = useCallback(() => {
    setState((s) => ({ ...s, muted: !s.muted }))
  }, [])

  const toggleShuffle = useCallback(() => {
    setState((s) => ({ ...s, shuffle: !s.shuffle }))
  }, [])

  const setRepeat = useCallback((mode: RepeatMode) => {
    setState((s) => ({ ...s, repeat: mode }))
  }, [])

  const setCrossfadeDuration = useCallback((seconds: number) => {
    const value = Number.isFinite(seconds) ? clampCrossfade(seconds) : 0
    useAppStore.getState().setPlayerSettings({ crossfadeDuration: value })
  }, [])

  const setReplayGainEnabled = useCallback((enabled: boolean) => {
    useAppStore.getState().setPlayerSettings({ replayGainEnabled: enabled })
  }, [])

  const setRadioMode = useCallback((enabled: boolean) => {
    if (enabled) {
      setState((s) => ({
        ...s,
        queue: s.queue.filter((t, i) => i <= s.currentIndex || !t.radioGenerated),
      }))
      useAppStore.getState().setRadioSettings({ moodTargets: DEFAULT_MOOD_TARGETS })
      useAppStore.getState().resetRadioFeedback()
    }
    useAppStore.getState().setPlayerSettings({ radioMode: enabled })
  }, [])

  const setRadioFollow = useCallback((enabled: boolean) => {
    useAppStore.getState().setPlayerSettings({ radioFollow: enabled })
  }, [])

  useRadioMode({ state, addToQueue, pendingAdvanceRef: pendingRadioAdvanceRef, advanceOne })
  useInterstitialMode({ state, insertAt, subscribeCurrentTime, getCurrentTime })

  const nextIndexInput: NextIndexInput = useMemo(
    () => ({
      queue: state.queue,
      currentIndex: state.currentIndex,
      radioMode: state.radioMode,
      shuffle: state.shuffle,
      repeat: state.repeat,
    }),
    [state.queue, state.currentIndex, state.radioMode, state.shuffle, state.repeat]
  )

  // Pre-load next track into the inactive channel so transitions are gapless.
  // Runs whenever the current track or queue changes. Skipped while a crossfade
  // is in progress (crossfade owns the inactive channel during that window).
  // The only other values it touches are stable ref objects.
  // biome-ignore lint/correctness/useExhaustiveDependencies: nextIndexInput covers every state field read here
  useEffect(() => {
    if (crossfadeActiveRef.current) return

    const nextResult = getNextIndex(nextIndexInput)
    if (nextResult.type !== 'advance') return

    const nextTrack = nextIndexInput.queue[nextResult.index]
    if (!nextTrack || nextTrack.isInterstitial) return

    const inactive = activeChannelRef.current === 'A' ? channelBRef.current : channelARef.current
    if (!inactive) return

    const nextSrc = nextTrack.streamOverride ?? streamUrl(nextTrack.id)
    if (inactive.audio.getAttribute('data-preload') === nextSrc) return

    inactive.audio.setAttribute('data-preload', nextSrc)
    inactive.audio.src = nextSrc
    if (inactive.gainNode) {
      const now = inactive.gainNode.context.currentTime
      inactive.gainNode.gain.cancelScheduledValues(now)
      inactive.gainNode.gain.setValueAtTime(0, now)
    }
  }, [nextIndexInput])
  useDesktopNotifications({
    enabled: playerSettings.desktopNotifications,
    queue: state.queue,
    currentIndex: state.currentIndex,
  })

  const value: PlayerContextValue = {
    state,
    getAnalyserNode,
    getCurrentTime,
    subscribeCurrentTime,
    play,
    pause,
    resume,
    togglePlay,
    next,
    prev,
    seek,
    setVolume,
    toggleMute,
    toggleShuffle,
    setRepeat,
    setCrossfadeDuration,
    setReplayGainEnabled,
    setRadioMode,
    setRadioFollow,
    enqueue,
    addToQueue,
    removeFromQueue,
    moveInQueue,
    clearQueue,
    playTrackAt,
  }

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
}
